import type { AnimationEngine } from 'types/animation'

function isAnimationGroup(element: HTMLElement): boolean {
  return element.dataset.animateGroup !== undefined
}

function cleanUpElement(element: HTMLElement, animationClass: string): void {
  element.removeAttribute('data-animate')
  element.classList.remove(animationClass)
  element.style.removeProperty('animation-delay')
}

function revealElement(element: HTMLElement, delay: number = 0): void {
  const animationClass = getAnimationClass(element.dataset.animate)

  if (delay > 0) {
    element.style.animationDelay = `${delay}ms`
  }

  element.addEventListener('animationend', () => {
    cleanUpElement(element, animationClass)
  }, { once: true })

  element.classList.add(animationClass)
}

function revealGroup(group: HTMLElement): void {
  const children = group.querySelectorAll<HTMLElement>(ANIMATION_ELEMENT_SELECTOR)

  children.forEach((child, index) => revealElement(child, getStaggerDelay(index)))
}

function revealTarget(target: HTMLElement): void {
  if (isAnimationGroup(target)) {
    revealGroup(target)
    return
  }

  revealElement(target)
}

function handleIntersections(entries: IntersectionObserverEntry[], observer: IntersectionObserver): void {
  entries.forEach(entry => {
    if (!entry.isIntersecting) {
      return
    }

    observer.unobserve(entry.target)
    revealTarget(entry.target as HTMLElement)
  })
}

function whenScrollSettles(onSettled: () => void): void {
  const deadline = performance.now() + ANIMATION_SCROLL_SETTLE_TIMEOUT_MS

  let lastPosition = window.scrollY
  let stableFrames = 0

  function check(): void {
    const position = window.scrollY

    stableFrames = position === lastPosition ? stableFrames + 1 : 0
    lastPosition = position

    const isSettled = stableFrames >= ANIMATION_SCROLL_SETTLE_FRAMES
    const isExpired = performance.now() >= deadline

    if (isSettled || isExpired) {
      onSettled()
      return
    }

    requestAnimationFrame(check)
  }

  requestAnimationFrame(check)
}

export function useAnimationEngine(): AnimationEngine {
  const observedElements = new WeakSet<HTMLElement>()

  let isWaitingForScroll = false

  const viewportObserver = new IntersectionObserver(handleIntersections, {
    rootMargin: ANIMATION_ROOT_MARGIN,
    threshold: ANIMATION_THRESHOLD
  })

  // Nuxt page hooks fire before the out-in transition swaps the DOM, so the DOM itself is the trigger.
  const pageObserver = new MutationObserver(() => {
    scanWhenReady()
  })

  function observeElement(element: HTMLElement): void {
    if (observedElements.has(element)) {
      return
    }

    observedElements.add(element)
    viewportObserver.observe(element)
  }

  function scan(): void {
    document
      .querySelectorAll<HTMLElement>(ANIMATION_SELECTOR)
      .forEach(observeElement)
  }

  // Scanning mid-flight would reveal every section the scroll-to-top flies over.
  function scanWhenReady(): void {
    if (isWaitingForScroll) {
      return
    }

    isWaitingForScroll = true

    whenScrollSettles(() => {
      isWaitingForScroll = false
      scan()
    })
  }

  function start(): void {
    scanWhenReady()

    // Anchored on the body: leaving the error page rebuilds the layout, detaching any inner container observed earlier.
    pageObserver.observe(document.body, { childList: true, subtree: true })
  }

  return {
    start
  }
}
